import React, { useState } from 'react';
import Button from 'components/Button/Button';
import { icons } from 'assets/icons';
import AntModalStyled from 'components/Modals/AntModalStyled';
import styled from 'styled-components';
import { colours } from 'styling/colours';
import { Horizontal, StretchSpacer } from 'gls/lib';
import { Controller, useForm } from 'react-hook-form';
import { programaticallyDownloadFileFromHref } from 'utils/utils';
import { subMonths } from 'date-fns';
import { alertErrorMessage, alertSuccessMessage } from 'utils/alerts';
import { getISOFormatStartEndDates, getStartEndDatesMessage } from 'utils/date';
import { CreateLogExportJob, GetLogExportJobStatus } from 'api/devices.api';
import { DownloadStatusEnum } from 'types/asset';
import { DatePicker } from 'components/Dates/DatePicker';
import moment from 'moment';

type ModalProps = {
  onClose: () => void;
};

const StyledIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 1px solid ${colours.secondaryText};
  border-radius: 50%;
  img {
    width: 20px;
  }
`;

const StyledBody = styled.div`
  margin: 30px 0 70px;
`;

type downloadFormType = {
  startDate: number | Date;
  endDate: number | Date;
  name: string;
};

export const DownloadLogsModal = (props: ModalProps) => {
  const {
    control,
    getValues,
    watch,
    resetField,
    formState: { isValid },
  } = useForm<downloadFormType>({
    defaultValues: {
      startDate: undefined,
      endDate: undefined,
    },
  });
  const startDate = watch('startDate');
  const endDate = watch('endDate');

  const [downloadStatus, setDownloadStatus] = useState<DownloadStatusEnum | null>(null);
  const [apiCallInProgress, setApiCallInProgress] = useState(false);

  function handleStatusResponse(response: any, interval?: any) {
    switch (response.status) {
      case DownloadStatusEnum.processing:
        break;

      case DownloadStatusEnum.export_succeeded:
        clearInterval(interval);
        programaticallyDownloadFileFromHref(response.url);
        setApiCallInProgress(false)
        alertSuccessMessage(getStartEndDatesMessage(
          getValues().startDate,
          getValues().endDate,
          "Asset Logs",
          "log has been downloaded successfully"));
        closeHandler();
        break;

      case DownloadStatusEnum.failed:
        clearInterval(interval);
        handleError(response);
        closeHandler();

        break;

      default:
        break;
    }
  }
  function handleError(err: any) {
    console.log(err);
    setApiCallInProgress(false);
    alertErrorMessage('Sorry, the job failed and your file has not been created.');
  }

  const formSubmitHandler = () => {
    setApiCallInProgress(true);
    const { startISOString, endISOString } = getISOFormatStartEndDates(
      getValues().startDate,
      getValues().endDate,
    );

    CreateLogExportJob(startISOString, endISOString)
      .then((res) => {
        setDownloadStatus(DownloadStatusEnum.processing);
        const interval = setInterval(() => {
          GetLogExportJobStatus(res.job_id).then((res) => {
            setDownloadStatus(res.status);
            handleStatusResponse(res, interval);
          });
        }, 1000);
      })
      .catch((err) => handleError(err));
  };

  const closeHandler = () => {
    resetField('startDate');
    resetField('endDate');
    props.onClose();
  };


  return (
    <AntModalStyled onCancel={closeHandler} open centered footer={null} maskClosable={false}>
      <header>
        <StyledIconWrapper>
          <icons.DownloadIcon2 aria-label='download' fill={colours.secondaryText} />
        </StyledIconWrapper>
        <h3>
          {!downloadStatus && 'Download Custom Log'}
          {downloadStatus === DownloadStatusEnum.processing && 'Download in Progress'}
        </h3>
      </header>
      <StyledBody id='modal-content'>
        {!downloadStatus && (
          <Horizontal>
            <Controller
              name="startDate"
              control={control}
              rules={{ required: true }}
              render={({ field }: any) => (
                <DatePicker
                  {...field}
                  popupStyle={{ maxWidth: '300px', zIndex: '999999' }}
                  required
                  key={field.value}
                  label="Start Date:"
                  id='start-date-input'
                  onChange={(date) => {
                    field.onChange(date);
                    if (endDate && moment(date)?.isAfter(moment(endDate))) {
                      resetField('endDate');
                    }
                  }}
                  minDate={subMonths(new Date(), 2)}
                  maxDate={new Date()}
                />
              )}
            />
            <StretchSpacer />
            <Controller
              name="endDate"
              control={control}
              rules={{ required: true }}
              render={({ field }: any) => (
                <DatePicker
                  {...field}
                  popupStyle={{ maxWidth: '300px', zIndex: '999999' }}
                  required
                  id='end-date-input'
                  label="End Date:"
                  onChange={field.onChange}
                  key={field.value}
                  minDate={startDate ? new Date(startDate) : subMonths(new Date(), 2)}
                  maxDate={new Date()}
                />
              )}
            />
          </Horizontal>
        )}
        {downloadStatus === DownloadStatusEnum.processing && (
          <p>The file is being prepared. Please do not navigate away from the page.</p>
        )}
      </StyledBody>
      <footer id='modal-footer'>
        {downloadStatus !== DownloadStatusEnum.processing && (
          <Button id="archiveAssetCancelButton" onClick={closeHandler} outline>
            Cancel
          </Button>
        )}
        <Button
          isLoading={apiCallInProgress}
          disabled={!isValid}
          id="download-button"
          onClick={formSubmitHandler}
        >
          Download
        </Button>
      </footer>
    </AntModalStyled>
  );
};
